import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Loader2, Wand2 } from 'lucide-react';
import { PageLayout } from '../components/layout';
import { UpgradeModal } from '../components/modals';
import { PersonaAnalysisCard } from '../components/personas/PersonaAnalysisCard';
import { PersonaCard } from '../components/personas/PersonaCard';
import PersonaForm from '../components/personas/PersonaForm';
import { CreatePersonaModal } from '../components/personas/CreatePersonaModal';

import { BrandSelectorModal } from '../components/personas/BrandSelectorModal';
import { BrandDropdown } from '../components/common/BrandDropdown';
import { DeleteConfirmationModal } from '../components/common/DeleteConfirmationModal';
import { BrandSetupErrorModal } from '../components/common/BrandSetupErrorModal';
import { usePersonas, usePersonaById } from '../hooks/usePersonas';
import { useCreatePersona, useUpdatePersona, useDeletePersona } from '../hooks/usePersonaMutations';
import { usePersonaGeneration } from '../hooks/usePersonaGeneration';
import { 
  Persona, 
  PersonaData, 
  PersonaGenerationResponse, 
  SocialNetwork,
  CreatePersonaRequest,
  UpdatePersonaRequest
} from '../types/personas';
import { useBrands } from '../hooks/useBrands';
import { Brand } from '../types/brands';
import { useBrandContext } from '../contexts/BrandContext';
import { GifLoader } from '../components/website/GifLoader';
import { useMediaApi } from '../hooks/useMediaApi';
import { isIdeogramUrl, uploadExternalImageUrl } from '../utils/imageUtils';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useUrlStringState } from '../hooks/useUrlState';
import { useAuth } from '../hooks/useAuth';
import { usePersonaAnalysis } from '../hooks/usePersonaAnalysis';
import { api } from '../services/api';
import { auth } from '../config/firebase';
import { logger } from '../utils/logger';
import { useToast } from '../context/ToastContext';

/**
 * Convert language code to 2-letter ISO 639-1 format
 * Examples: "fr-FR" -> "fr", "en-US" -> "en", "fr" -> "fr"
 */
const normalizeLanguageCode = (language: string): string => {
  if (!language) return 'en';
  
  // Extract the first 2 characters (language code) and convert to lowercase
  const normalized = language.substring(0, 2).toLowerCase();
  
  // Validate that it's a valid 2-letter code
  if (normalized.length === 2 && /^[a-z]{2}$/.test(normalized)) {
    return normalized;
  }
  
  // Fallback to English if invalid
  return 'en';
};

// Simplified AI Loading State - Only for AI Analysis and Generation
const PersonaAILoadingState: React.FC = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<string>('');
  const [stepIndex, setStepIndex] = useState(0);

  const title = t('personas.loading.aiGeneration', "AI is working its magic...");
  
  const loadingSteps = [
    { message: t('personas.loading.steps.analyzing', "Analyzing your personas data..."), duration: 2000 },
    { message: t('personas.loading.steps.processing', "Processing demographic insights..."), duration: 2000 },
    { message: t('personas.loading.steps.generating', "Generating AI recommendations..."), duration: 2000 },
    { message: t('personas.loading.steps.preparing', "Preparing persona profiles..."), duration: 2000 },
  ];

  useEffect(() => {
    // Set first step initially
    setCurrentStep(loadingSteps[0].message);

    // Set up the step transition
    const intervalId = setInterval(() => {
      setStepIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % loadingSteps.length;
        setCurrentStep(loadingSteps[nextIndex].message);
        return nextIndex;
      });
    }, 2000); // Change step every 2 seconds

    return () => clearInterval(intervalId);
  }, []);

  return (
    <>
      {/* Unicorn GIF overlay */}
      <GifLoader
        title={title}
        currentStep={currentStep}
        videoSrc="/Panda Socialness.ai - Casting a spell.mov"
      />
      
      {/* Skeleton content underneath */}
      <PersonasSkeletonLoader />
    </>
  );
};

// Create a skeleton loader component for the Personas page
const PersonasSkeletonLoader: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Skeleton loader for the AI insights card */}
      <div className="mb-8">
        <div className="rounded-xl border border-black shadow-lg bg-white overflow-hidden h-full animate-pulse">
          <div className="p-6 border-b border-gray-200 bg-gray-100">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          </div>
          <div className="p-6">
            <div className="flex flex-col">
              <div className="mb-6">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
              </div>
              
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 border-2 border-gray-100 rounded-lg p-4">
                  <div className="h-3 bg-gray-200 rounded w-1/3 mb-3"></div>
                  <div className="h-2 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-2 bg-gray-200 rounded w-4/5 mb-2"></div>
                  <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-2 bg-gray-200 rounded w-full"></div>
                </div>
                
                <div className="flex-1 border-2 border-gray-100 rounded-lg p-4">
                  <div className="h-3 bg-gray-200 rounded w-1/3 mb-3"></div>
                  <div className="h-2 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-2 bg-gray-200 rounded w-4/5 mb-2"></div>
                  <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-2 bg-gray-200 rounded w-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Skeleton loader for persona cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-8">
        {Array(4).fill(0).map((_, index) => (
          <div key={`persona-skeleton-${index}`} className="rounded-xl border border-black shadow-lg bg-white p-6 animate-pulse">
            <div className="flex items-start mb-4">
              <div className="w-16 h-16 bg-gray-200 rounded-full mr-4"></div>
              <div className="flex-1">
                <div className="h-5 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="flex gap-2 mb-3">
                  {Array(3).fill(0).map((_, i) => (
                    <div key={`skeleton-tag-${index}-${i}`} className="h-6 bg-gray-200 rounded px-3 py-1"></div>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="h-3 bg-gray-200 rounded w-full"></div>
              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
              <div className="h-3 bg-gray-200 rounded w-4/5"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Personas = () => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { personaId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<PersonaData | null>(null);
  const [modalParam, setModalParam] = useUrlStringState('modal', '');
  const isCreateModalOpen = modalParam === 'create';
  const isUpgradeModalOpen = modalParam === 'upgrade';
  const [isBrandSelectorOpen, setIsBrandSelectorOpen] = useState(false);
  const [isGeneratingPersona, setIsGeneratingPersona] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [personaToDelete, setPersonaToDelete] = useState<string | null>(null);
  // New state variables to track loading states for CRUD operations
  const [isCreatingPersona, setIsCreatingPersona] = useState(false);
  const [isUpdatingPersona, setIsUpdatingPersona] = useState(false);
  const [isDeletingPersona, setIsDeletingPersona] = useState(false);
  const [withAnalysis, setWithAnalysis] = useState(false);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [returnToBrands, setReturnToBrands] = useState(false);
  const [showNoPersonasModal, setShowNoPersonasModal] = useState(false);
  const [brandSetupError, setBrandSetupError] = useState<{
    isOpen: boolean;
    brandId?: string;
    errorData?: any;
  }>({ isOpen: false });
  
  // Get the current brand ID from the brands hook
  const { data: brandsData } = useBrands();
  // Use type assertion to access the brands property
  const brands = brandsData ? (brandsData as any).brands || [] : [];
  const { currentBrandId } = useBrandContext();
  
  // Create brands for dropdown (will be filtered after personas are loaded)
  const formattedBrands: Brand[] = brands.map((b: any) => ({
    id: b.id, 
    name: b.name, 
    logo: b.logo
  }));
  const allBrandsOption: Brand = { id: 'all', name: 'All Brands', logo: '/logo.svg' };
  


  // Log for debugging
  useEffect(() => {
    logger.info('Personas', `Current brand ID: ${currentBrandId}`);
    logger.info('Personas', `Available brands count: ${brands?.length || 0}`);
    logger.info('Personas', `Selected brand for filter: ${selectedBrand}`);
  }, [currentBrandId, brands, selectedBrand]);
  
  // Initialize queries and mutations for personas - Load all personas once
  const { 
    data, 
    isLoading, 
    isError,
    error,
    refetch: refetchPersonas 
  } = usePersonas(
    undefined, // No server-side filtering by brand - we'll filter client-side
    false, // useMockData: force real API data
    withAnalysis
  );
  const createPersonaMutation = useCreatePersona();
  const updatePersonaMutation = useUpdatePersona();
  const deletePersonaMutation = useDeletePersona();
  const personaGenerationMutation = usePersonaGeneration();
  // Optional fetch of a single persona when opening via routed URL
  const { data: personaByIdData } = usePersonaById(personaId, false);
  
  // Initialize the media API for image uploads
  const mediaApi = useMediaApi();
  
  // Extract personas from the API response
  const personas = useMemo(() => {
    if (!data) return [];
    // Debug: Uncomment the following lines for detailed API payload visualization
    // logger.debug('Personas', 'API response', data);
    // logger.debug('Personas', 'Raw personas array', data.personas);
    return (data.personas || []) as Persona[];
  }, [data]);

  // Limits and counts
  const totalPersonas = data?.pagination?.total ?? personas.length;
  const personaLimits = (data as any)?.personaLimits ?? (data as any)?.personasLimit ?? undefined;
  
  // Add debug logging for personas loading
  useEffect(() => {
    // Keep this effect lean – only log in debug mode
    if (process.env.NODE_ENV === 'development') {
      logger.debug('Personas', `Loading: ${isLoading} | Error: ${isError}`);
      if (error) {
        logger.error('Personas', 'Error details', error);
      }
      logger.debug('Personas', 'Data snapshot', data);
      logger.debug('Personas', `Current personas count: ${personas.length}`);
    }
  }, [isLoading, isError, error, data, personas]);
  
  // Handle navigation from BrandCard
  useEffect(() => {
    const state = location.state as any;
    if (state) {
      const { selectedBrandId, createWithAI, createManually, editPersonaId, returnToBrands: shouldReturnToBrands } = state;
      
      if (selectedBrandId) {
        setSelectedBrand(selectedBrandId);
      }
      
      if (shouldReturnToBrands) {
        setReturnToBrands(true);
      }
      
      if (editPersonaId) {
        // Prefer URL routing for edit
        navigate(`/ai-personas-builder/${editPersonaId}/edit`, { replace: true });
        } else if (createWithAI) {
        // Open AI creation flow
        if (selectedBrandId) {
          generatePersonaWithAI(selectedBrandId);
        } else {
          setModalParam('create');
        }
      } else if (createManually) {
        // Open manual creation via routed URL
        navigate('/ai-personas-builder/new', { replace: true });
      }
      
      // Clear the state after handling
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, personas]);

  // Open form when visiting routed URLs for creation
  useEffect(() => {
    if (location.pathname.endsWith('/ai-personas-builder/new')) {
      setSelectedPersona(null);
      setShowForm(true);
    }
  }, [location.pathname]);

  // If personaId in URL, try to load from already-fetched list
  useEffect(() => {
    if (personaId) {
      const p = personas.find(p => p.id === personaId);
      if (p) {
        setSelectedPersona(convertToPersonaData(p));
        setShowForm(true);
      }
    }
  }, [personaId, personas]);

  // Fallback: if not in list, use single-persona API response
  useEffect(() => {
    if (personaId && personaByIdData?.persona) {
      setSelectedPersona(convertToPersonaData(personaByIdData.persona as any));
      setShowForm(true);
    }
  }, [personaId, personaByIdData]);
  
  // Filter brands to only show those with personas
  const brandsWithPersonas = useMemo(() => {
    if (!personas.length) return formattedBrands; // If no personas, show all brands for creation
    
    // Get unique brand IDs from personas
    const brandIds = [...new Set(personas.map(p => p.brandId))];
    
    // Filter brands to only include those with personas
    return formattedBrands.filter((b: Brand) => brandIds.includes(b.id));
  }, [formattedBrands, personas]);
  
  // Get global analysis if available
  const globalAnalysis = (data as any)?.globalAnalysis;
  
  logger.info('Personas', `Total personas found: ${personas.length}`);
  if (globalAnalysis) {
    logger.info('Personas', 'Global analysis data loaded');
  }
  
  // Process analysis data for the AIReport component
  let summaryText = personas.length > 0 
    ? `You have ${personas.length} persona${personas.length > 1 ? 's' : ''} configured. Click "Analyze with AI" to get insights.`
    : "No personas created yet. Create your first persona to get started.";
  let strengthsText = personas.length > 0 ? "Click 'Analyze with AI' to get detailed insights" : "N/A";
  let improvementsText = personas.length > 0 
    ? "Analysis will help identify areas for improvement" 
    : "Consider creating personas to better understand your target audience.";
  
  if (globalAnalysis) {
    // Use the global analysis data from the API if available
    summaryText = globalAnalysis.summary || summaryText;
    
    // Convert strengths array to string with numbered points
    if (globalAnalysis.strengths && Array.isArray(globalAnalysis.strengths)) {
      strengthsText = globalAnalysis.strengths.map((strength: string, index: number) => 
        `${index + 1}. ${strength}`
      ).join('\n');
    }
    
    // Convert areasOfImprovement array to string with numbered points
    if (globalAnalysis.areasOfImprovement && Array.isArray(globalAnalysis.areasOfImprovement)) {
      improvementsText = globalAnalysis.areasOfImprovement.map((improvement: string, index: number) => 
        `${index + 1}. ${improvement}`
      ).join('\n');
    }
  } else if (personas.length > 0 && personas[0].analysis) {
    // Use type assertion for the entire analysis object since API format differs from our type
    const analysis = personas[0].analysis as any;
    
    logger.info('Analysis data from first persona:', analysis);
    
    // Check for each property with fallbacks
    summaryText = analysis.summary || `Analysis for ${personas.length} persona(s)`;
    
    // Format strengths with numbered points
    if (analysis.strengths) {
      // If it's already an array, format each item with numbers
      if (Array.isArray(analysis.strengths)) {
        strengthsText = analysis.strengths.map((strength: string, index: number) => 
          `${index + 1}. ${strength}`
        ).join('\n');
      } else {
        // If it's a string, add a number at the beginning
        strengthsText = `1. ${analysis.strengths}`;
      }
    } else {
      strengthsText = "N/A";
    }
    
    // Format weaknesses/improvements with numbered points
    if (analysis.weaknesses) {
      // If it's already an array, format each item with numbers
      if (Array.isArray(analysis.weaknesses)) {
        improvementsText = analysis.weaknesses.map((weakness: string, index: number) => 
          `${index + 1}. ${weakness}`
        ).join('\n');
      } else {
        // If it's a string, add a number at the beginning
        improvementsText = `1. ${analysis.weaknesses}`;
      }
    } else {
      improvementsText = "Consider adding more detailed information to your personas.";
    }
  }
  
  // Filter personas based on search query and selected brand (client-side filtering)
  const filteredPersonas = personas.filter((persona: Persona) => {
    // Search filter - check name, job title, and tags
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || (
      persona.name.toLowerCase().includes(searchLower) ||
      persona.jobTitle.toLowerCase().includes(searchLower) ||
      persona.tags.some((tag: string) => tag.toLowerCase().includes(searchLower))
    );
    
    // Brand filter - show all brands or specific brand
    const matchesBrand = selectedBrand === 'all' ? true : persona.brandId === selectedBrand;
    
    return matchesSearch && matchesBrand;
  });

  // Convert Persona to PersonaData for the form
  const convertToPersonaData = (persona: Persona): PersonaData => {
    // Parse socialNetworks from JSON strings if needed
    let socialNetworks = [];
    if (persona.socialNetworks && Array.isArray(persona.socialNetworks)) {
      logger.info('Personas', 'Raw socialNetworks from API:', persona.socialNetworks);
      
      socialNetworks = persona.socialNetworks
        .map(sn => {
          // If it's already an object, return it
          if (typeof sn === 'object' && sn.platform) {
            logger.info('Personas', 'SocialNetwork already object:', sn);
            return sn;
          }
          // If it's a JSON string, parse it
          if (typeof sn === 'string') {
            try {
              const parsed = JSON.parse(sn);
              logger.info('Personas', 'Parsed socialNetwork JSON:', parsed);
              return parsed.platform ? parsed : null;
            } catch (error) {
              logger.error('Personas', 'Error parsing socialNetwork JSON:', error);
              return null;
            }
          }
          logger.warn('Personas', 'Unknown socialNetwork format:', sn);
          return null;
        })
        .filter(sn => sn !== null); // Filter out null entries
        
      logger.info('Personas', 'Final parsed socialNetworks:', socialNetworks);
    }

    return {
      id: persona.id,
      name: persona.name,
      jobTitle: persona.jobTitle,
      bio: persona.bio,
      tags: persona.tags || [],
      age: persona.age || 28,
      ageRange: persona.ageRange,
      languages: persona.languages || [],
      socialNetworks: socialNetworks,
      problems: persona.problems,
      goals: persona.goals,
      motivations: persona.motivations,
      challenges: persona.challenges,
      formatLength: persona.formatLength || [],
      avatarUrl: persona.avatarUrl,
      location: persona.location,
      income: persona.income,
      education: persona.education,
      relationship: persona.relationship,
      quote: persona.quote,
      buying_behavior: persona.buying_behavior,
      brand_affinity: persona.brand_affinity,
      brandIds: [persona.brandId]
    };
  };

  const handleSubmit = async (data: PersonaData) => {
    const brandId = data.brandIds[0] || currentBrandId || '';
    
    logger.info('Personas', 'Submitting persona data', { name: data.name, brandId });

    if (!data.id || data.id.startsWith('local-')) {
      logger.info('Personas', 'Creating new persona (detected local ID)');
      
      // Remove the temporary ID when creating a new persona
      const { id, ...dataWithoutId } = data;
      
      // Prepare the request
      const apiData: CreatePersonaRequest = {
        name: data.name,
        brandId: brandId,
        jobTitle: data.jobTitle || '',
        bio: data.bio || '',
        tags: data.tags || [],
        age: data.age || 25,
        languages: data.languages || [],
        socialNetworks: data.socialNetworks || [],
        ageRange: data.ageRange || '25-34',
        problems: data.problems || [],
        goals: data.goals || [],
        motivations: data.motivations || [],
        challenges: data.challenges || [],
        formatLength: data.formatLength || [],
        // Add missing fields that were present in the form but not sent to API
        location: data.location || '',
        income: data.income || '',
        education: data.education || '',
        relationship: data.relationship || '',
        quote: data.quote || '',
        buying_behavior: data.buying_behavior || '',
        brand_affinity: data.brand_affinity || ''
      };

      // Set the correct avatarURL property for the API
      if (data.avatarUrl) {
        logger.info('Personas', `Avatar URL detected: ${data.avatarUrl.length} characters`);
        apiData.avatarURL = data.avatarUrl;
      }

      setIsCreatingPersona(true);

      try {
        await createPersonaMutation.mutateAsync(apiData);
        logger.info('Personas', 'Successfully created persona');

        try {
          // Force a complete refetch and reset analysis state
          setWithAnalysis(false);
          setAnalysisData(null);
          await refetchPersonas();
        } catch (error) {
          logger.error('Personas', 'Error refetching personas after creation', error);
        }

        setShowForm(false);
        setSelectedPersona(null);
        setIsCreatingPersona(false);
        // If opened via routed URL, go back to list
        if (location.pathname.startsWith('/ai-personas-builder/')) {
          navigate('/ai-personas-builder');
        }

        // Navigate back to brands if requested
        if (returnToBrands) {
          navigate('/brand-strategy-lab');
        }
      } catch (error: any) {
        logger.error('Personas', 'Error creating persona', error);
        if (error.response) {
          logger.error('Personas', `Error response status: ${error.response.status}`);
        }

        // If it failed with the simple approach, try with the next most minimal approach
        logger.info('Personas', 'Trying with absolute minimal data');
        const minimalData: CreatePersonaRequest = {
          name: data.name,
          brandId: brandId,
          jobTitle: data.jobTitle || '',
          bio: data.bio || '',
          tags: [],
          age: 25,
          languages: [],
          socialNetworks: [],
          ageRange: '25-34',
          problems: [],
          goals: [],
          motivations: [],
          challenges: [],
          formatLength: []
        };

        logger.info('Personas', 'Submitting minimal API data');

        try {
          await createPersonaMutation.mutateAsync(minimalData);
          logger.info('Personas', 'Successfully created persona with minimal data');

          try {
            // Force a complete refetch and reset analysis state
            setWithAnalysis(false);
            setAnalysisData(null);
            await refetchPersonas();
          } catch (refetchError) {
            logger.error('Personas', 'Error refetching personas after minimal creation', refetchError);
          }

          setShowForm(false);
          setSelectedPersona(null);
          setIsCreatingPersona(false);
          if (location.pathname.startsWith('/ai-personas-builder/')) {
            navigate('/ai-personas-builder');
          }

          // Navigate back to brands if requested
          if (returnToBrands) {
            navigate('/brand-strategy-lab');
          }
        } catch (secondError) {
          logger.error('Personas', 'Error creating persona with minimal data', secondError);
          alert(`Failed to create persona: ${error}`);
          setIsCreatingPersona(false);
        }
      }
    } else {
      logger.info('Personas', 'Updating existing persona');
      // Update existing persona
      setIsUpdatingPersona(true);

      try {
        await updatePersonaMutation.mutateAsync(data as UpdatePersonaRequest);
        logger.info('Personas', 'Successfully updated persona');
        // Force a complete refetch and reset analysis state
        setWithAnalysis(false);
        setAnalysisData(null);
        await refetchPersonas();
        setShowForm(false);
        setSelectedPersona(null);
        setIsUpdatingPersona(false);
        if (location.pathname.startsWith('/ai-personas-builder/')) {
          navigate('/ai-personas-builder');
        }

        // Navigate back to brands if requested
        if (returnToBrands) {
          navigate('/brand-strategy-lab');
        }
      } catch (error) {
        logger.error('Personas', 'Error updating persona', error);
        alert(`Failed to update persona: ${error}`);
        setIsUpdatingPersona(false);
      }
    }
  };
  
  const handleDelete = (personaId: string) => {
    setPersonaToDelete(personaId);
  };

  const confirmDelete = () => {
    if (personaToDelete) {
      setIsDeletingPersona(true);
      
              deletePersonaMutation.mutate(personaToDelete, {
        onSuccess: async () => {
          try {
            // Force a complete refetch and reset analysis state
            setWithAnalysis(false);
            setAnalysisData(null);
            await refetchPersonas();
            setPersonaToDelete(null);
            setIsDeletingPersona(false);
          } catch (error) {
            logger.error('Personas', 'Error refetching personas after deletion', error);
            setPersonaToDelete(null);
            setIsDeletingPersona(false);
          }
        },
        onError: (error) => {
          logger.error('Personas', 'Error deleting persona', error);
          setPersonaToDelete(null);
          setIsDeletingPersona(false);
        }
      });
    }
  };

  const handleCreateWithAI = () => {
    // Check if we have brands available
    if (brands.length === 0) {
      alert(t('personas.errors.noBrands', 'No brands available. Please create a brand first.'));
      return;
    }
    
    // If there's only one brand, use it directly
    if (brands.length === 1) {
      logger.info('Personas', 'Using single brand for persona generation');
      generatePersonaWithAI(brands[0].id);
    } else {
      // If there are multiple brands, show the brand selector
      setModalParam('');
      setIsBrandSelectorOpen(true);
    }
  };

  const generatePersonaWithAI = async (brandId: string) => {
    logger.debug('Personas', 'generatePersonaWithAI called', { brandId, type: typeof brandId, length: brandId?.length });
    if (!brandId) {
      alert(t('personas.errors.noBrandSelected', 'Please select a brand first'));
      return;
    }

    console.log('🔍 generatePersonaWithAI called with brandId:', brandId);
    console.log('🔍 brandId type:', typeof brandId);
    console.log('🔍 brandId length:', brandId?.length);
    logger.info('Personas', `Generating persona for brand ID: ${brandId}`);
    setIsGeneratingPersona(true);
    setIsBrandSelectorOpen(false);

    try {
      await personaGenerationMutation.mutate(
        {
          brandId: brandId,
          language: normalizeLanguageCode(i18n.language || 'en')
        },
        {
          onSuccess: async (data: PersonaGenerationResponse) => {
            try {
              logger.info('Personas', 'Successfully generated persona');
              const generatedPersona = data.persona;
            
              let finalImageUrl = generatedPersona.imageUrl; // Default to AI provided image URL
              
              /*
              // Temporarily commented out due to issues with mediaApi.uploadMedia typing/usage
              if (generatedPersona.imageUrl) {
                try {
                  logger.info('Personas', 'Converting image URL to file for storage');
                  
                  const response = await fetch(generatedPersona.imageUrl);
                  const blob = await response.blob();
                  const fileName = `persona-${Date.now()}.jpg`;
                  const imageFile = new File([blob], fileName, { type: 'image/jpeg' });
                  
                  logger.info('Personas', 'Uploading persona image to storage service');
                  
                  const uploadResult = await mediaApi.uploadMedia({ file: imageFile }, {
                    // folder: 'personas', // These options might be incorrect for uploadMedia
                    // resize: { width: 400, height: 400 }
                  });
                  
                  logger.info('Personas', 'Image uploaded successfully');
                  
                  if (uploadResult && typeof uploadResult === 'object' && 'url' in uploadResult && uploadResult.url) {
                    finalImageUrl = uploadResult.url as string;
                    logger.info('Personas', 'Using stored image URL');
                  }
                } catch (error) {
                  logger.error('Personas', 'Error uploading persona image', error);
                  logger.info('Personas', 'Continuing with original image URL from AI');
                }
              }
              */
              
              setSelectedPersona({
                id: `local-${Date.now()}`,
                name: generatedPersona.name || '',
                jobTitle: generatedPersona.jobTitle || '',
                bio: generatedPersona.bio || '',
                age: generatedPersona.age || 25,
                tags: generatedPersona.tags || [],
                languages: generatedPersona.language ? [generatedPersona.language] : [],
                socialNetworks: generatedPersona.social_platforms ? generatedPersona.social_platforms.map(sp => ({
                  platform: sp.platform,
                  pertinence: 'Primary',
                  frequency: 'daily'
                })) : [],
                problems: generatedPersona.problems || [],
                goals: generatedPersona.goals || [],
                values: generatedPersona.values || [],
                avatarUrl: finalImageUrl,
                brandIds: [brandId],
                location: generatedPersona.location || '',
                income: generatedPersona.income || '',
                education: generatedPersona.education || '',
                relationship: generatedPersona.relationship || '',
                challenges: generatedPersona.challenges || [],
                motivations: generatedPersona.motivations || [],
                quote: generatedPersona.quote || '',
                buying_behavior: generatedPersona.buying_behavior || '',
                brand_affinity: generatedPersona.brand_affinity || '',
                formatLength: generatedPersona.format || []
              });
              
              setShowForm(true);
              setIsGeneratingPersona(false);
            } catch (successError) {
              logger.error('Personas', 'Error in onSuccess callback:', successError);
              setIsGeneratingPersona(false);
              showToast(
                t('personas.errors.generationFailed', 'Failed to generate persona. Please try again.'),
                'error'
              );
            }
          },
          onError: (error: any) => {
            logger.error('Personas', 'Error generating persona', JSON.stringify(error));
            setIsGeneratingPersona(false);
            
            // Check if this is a brand validation error (400 status)
            if (error.status === 400 && error.errorData) {
              const errorData = error.errorData;
              if (errorData.code === 'brand_validation_failed' && errorData.requiredFields) {
                // Show specific modal for missing brand fields
                setBrandSetupError({
                  isOpen: true,
                  brandId: brandId,
                  errorData: errorData
                });
                return;
              }
            }
            
            // Fallback: try to parse from error message if errorData is not available
            if (error.message && error.message.includes('400')) {
              try {
                // Extract JSON from error message if it contains one
                const jsonMatch = error.message.match(/\{.*\}/);
                if (jsonMatch) {
                  const errorData = JSON.parse(jsonMatch[0]);
                  if (errorData.code === 'brand_validation_failed' && errorData.requiredFields) {
                    setBrandSetupError({
                      isOpen: true,
                      brandId: brandId,
                      errorData: errorData
                    });
                    return;
                  }
                }
              } catch (parseError) {
                logger.error('Personas', 'Failed to parse error JSON', parseError);
              }
            }
            
            // Generic error fallback
            showToast(
              t('personas.errors.generationFailed', 'Failed to generate persona. Please try again.'),
              'error'
            );
          }
        }
      );
    } catch (mutationError) {
      logger.error('Personas', 'Mutation error in generatePersonaWithAI:', mutationError);
      setIsGeneratingPersona(false);
      
      // Handle any uncaught errors from the mutation
      showToast(
        t('personas.errors.generationFailed', 'Failed to generate persona. Please try again.'),
        'error'
      );
    }
  };

  const handleCreateManually = () => {
    setModalParam('');
    setSelectedPersona(null);
    setShowForm(true);
  };

  const handleAnalyze = async () => {
    // Check if user has personas before analyzing
    if (personas.length === 0) {
      setShowNoPersonasModal(true);
      return;
    }

    try {
      setIsGeneratingPersona(true);
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      // Add language parameter to the request
      const language = normalizeLanguageCode(i18n.language || 'en');
      const url = `https://api.socialness.ai/ai-coach/personas/analyze?language=${language}`;
      
      logger.info('Personas', 'Calling analyze API:', { url, language });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      logger.info('Personas', 'API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Personas', 'API error response:', errorText);
        throw new Error(`Failed to analyze personas: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      logger.info('Personas', 'Analysis response:', data);
      
      if (data.analysis && data.analysis.globalAnalysis) {
        setAnalysisData(data.analysis.globalAnalysis);
        setWithAnalysis(true);
        logger.info('Personas', 'Analysis data set successfully');
      } else {
        logger.error('Personas', 'Invalid response structure:', data);
        throw new Error('Invalid response structure from analysis API');
      }
      
      setIsGeneratingPersona(false);
    } catch (error) {
      logger.error('Personas', 'Error analyzing personas:', error);
      setIsGeneratingPersona(false);
      alert(`Failed to analyze personas: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (showForm) {
    return (
      <PersonaForm
        initialData={selectedPersona || undefined}
        onSubmit={handleSubmit}
        onCancel={() => {
          setShowForm(false);
          setSelectedPersona(null);
        }}
      />
    );
  }

  return (
    <>
      <PageLayout
        title={t('personas.title', 'Personas')}
        subtitle={t('personas.subtitle')}
        actions={
          <div className="flex items-center gap-3">
            <button 
              onClick={handleAnalyze}
              disabled={isGeneratingPersona}
              className="inline-flex items-center px-4 py-2 bg-white text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingPersona ? (
                <Loader2 key="analyze-loading" className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Wand2 key="analyze-idle" className="w-5 h-5 mr-2" />
              )}
              {t('personas.analyzeWithAI', 'Analyze with AI')}
            </button>
            <button 
              onClick={() => {
                const atLimit = typeof personaLimits === 'number' && totalPersonas >= personaLimits;
                if (atLimit) {
                  setModalParam('upgrade');
                } else {
                  setModalParam('create');
                }
              }}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus key="create-icon" className="w-5 h-5 mr-2" />
              {t('personas.create')}
            </button>
          </div>
        }
      >

          {/* Search and filter container */}
          <div className="mb-8 flex items-end justify-between gap-4">
            {/* Search bar */}
            <div className="flex-grow relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search key="search-icon" className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('personas.search')}
                className="block w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-3 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            {/* Right column: counter above brand filter */}
            <div className="flex-shrink-0 flex flex-col items-end gap-2">
              <div className="text-black text-base font-semibold">
                {typeof personaLimits === 'number' ? (
                  <>{totalPersonas}/{personaLimits} {t('billing.features.personas', { count: personaLimits })}</>
                ) : (
                  <>{totalPersonas} {t('billing.features.personas', { count: totalPersonas })}</>
                )}
              </div>
              {/* Brand filter dropdown */}
              <BrandDropdown
                brands={[allBrandsOption, ...brandsWithPersonas]}
                selectedBrandId={selectedBrand}
                onBrandSelect={setSelectedBrand}
              />
            </div>
          </div>

          {isLoading || isCreatingPersona || isUpdatingPersona || isDeletingPersona ? (
            <PersonasSkeletonLoader />
          ) : isError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
              <h3 className="text-red-800 font-medium">{t('common.error')}</h3>
              <p className="text-red-600 text-sm mt-1">{error?.message || t('common.unknownError')}</p>
            </div>
          ) : (
            <>
              {(withAnalysis || analysisData || globalAnalysis) && (
                <div className="mb-8">
                  <PersonaAnalysisCard
                    title={t('personas.analysis.title')}
                    analysisData={analysisData || (globalAnalysis ? {
                      summary: globalAnalysis.summary || summaryText,
                      strengths: globalAnalysis.strengths || [],
                      areasOfImprovement: globalAnalysis.areasOfImprovement || [],
                      recommendedNewPersonas: globalAnalysis.recommendedNewPersonas || [],
                      demographicInsights: globalAnalysis.demographicInsights,
                      brandPersonaAlignment: globalAnalysis.brandPersonaAlignment,
                      gapAnalysis: globalAnalysis.gapAnalysis
                    } : undefined)}
                    withAnalysis={withAnalysis}
                    onAnalyze={handleAnalyze}
                    isAnalyzing={isGeneratingPersona}
                  />
                </div>
              )}

              {filteredPersonas.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">
                    {searchQuery 
                      ? t('personas.noSearchResults') 
                      : t('personas.noPersonas')}
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => setModalParam('create')}
                      className="mt-4 inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <Plus key="create-first-icon" className="w-5 h-5 mr-2" />
                      {t('personas.createFirst')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-8">
                  {filteredPersonas.map((persona: Persona) => {
                    // Reason: Ensure stable key and prevent DOM manipulation errors
                    const stableKey = `persona-${persona.id}-${persona.brandId || 'no-brand'}`;
                    return (
                      <PersonaCard
                        key={stableKey}
                        {...persona}
                        age={persona.age ?? 28}
                        onClick={() => {
                          navigate(`/ai-personas-builder/${persona.id}/edit`);
                        }}
                        onEdit={() => {
                          navigate(`/ai-personas-builder/${persona.id}/edit`);
                        }}
                        onDelete={() => handleDelete(persona.id)}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
      </PageLayout>

      {/* Create Persona Modal */}
      <CreatePersonaModal
        isOpen={isCreateModalOpen}
        onClose={() => setModalParam('')}
        onCreateWithAI={handleCreateWithAI}
        onCreateManually={handleCreateManually}
      />

      {/* Brand Selector Modal */}
      <BrandSelectorModal
        isOpen={isBrandSelectorOpen}
        onClose={() => setIsBrandSelectorOpen(false)}
        onBrandSelect={generatePersonaWithAI}
      />

      {/* AI Loading State - Only for AI actions (Analysis and Generation) */}
      {isGeneratingPersona && <PersonaAILoadingState />}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={personaToDelete !== null}
        onClose={() => setPersonaToDelete(null)}
        onConfirm={confirmDelete}
        title={t('personas.delete.title', 'Delete Persona')}
        message={t('personas.delete.message', 'Are you sure you want to delete this persona? This action cannot be undone.')}
        warning={t('personas.delete.warning', 'All associated data will be permanently removed.')}
      />

      {/* No Personas Modal */}
      {showNoPersonasModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => setShowNoPersonasModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-lg border-2 border-black shadow-xl max-w-md w-full p-6">
            {/* Header */}
            <div className="flex items-center mb-6">
              <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-yellow-100 mr-3">
                <Wand2 key="modal-wand-icon" className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {t('personas.noPersonasForAnalysis.title', 'Analyse des Personas')}
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="mb-6">
              <p className="text-gray-600">
                {t('personas.noPersonasForAnalysis.message', 'Il faut d\'abord que tu crées un persona pour pouvoir lancer une analyse IA.')}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={() => setShowNoPersonasModal(false)}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNoPersonasModal(false);
                  setModalParam('create');
                }}
                className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus key="modal-create-icon" className="w-4 h-4 mr-2 inline" />
                {t('personas.noPersonasForAnalysis.createCTA', 'Créer un Persona')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brand Setup Error Modal */}
      <BrandSetupErrorModal
        isOpen={brandSetupError.isOpen}
        onClose={() => setBrandSetupError({ isOpen: false })}
        brandId={brandSetupError.brandId}
        errorData={brandSetupError.errorData}
      />
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setModalParam('')}
        onUpgrade={() => navigate('/billing')}
        resourceKey="personas"
      />
    </>
  );
};

export default Personas;